import cn from 'classnames'
import { selectFinancialYear, selectPartnerId } from 'legacy/lut/features/partner360/configuration'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Switch } from 'core-components/library/checkbox'

import { IS_SHOW_TEXT_COMPLETENESS } from '../../config'
import { useWizardSave } from '../../context/WizardSaveContext'
import {
  changeWizardStep,
  setWizardIsLoading,
} from '../../features/planningWizard/planningWizardSlice'
import { useNotApplicablePlanningStep } from '../../hooks/useNotApplicablePlanningStep'
import { IWizardStep } from '../../models/IWizardStep'
import { CompletenessLabel } from '../completenessLabel/CompletenessLabel'
import { ConfirmationDialog } from '../confirmationDialog/ConfirmationDialog'

import { CompletenessIcon } from './components/completenessIcon/CompletenessIcon'
import { TRANSLATION_KEYS } from './translations'

import styles from './WizardStep.module.scss'

export interface IWizardStepProps {
  step: IWizardStep
  activeStepId: Nullable<string>
}

export const WizardStep: FC<IWizardStepProps> = memo(({ step, activeStepId }) => {
  /** ------------------------- State ------------------------- */

  const [isShowSwitchOffConfirmDialog, setIsShowSwitchOnConfirmDialog] = useState(false)
  const [isShowChangeStepConfirmDialog, setIsShowChangeStepConfirmDialog] = useState(false)
  const [isShowDisabledCategoryWarning, setIsShowDisabledCategoryWarning] = useState(false)
  const [isNeedDropCommitment, setIsNeedDropCommitment] = useState(false)

  /** ------------------------- Selectors ------------------------- */

  const fYear = useSelector(selectFinancialYear)
  const partnerId = useSelector(selectPartnerId)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()

  const wizardSave = useWizardSave()

  const { updatePlanningStepApplicable } = useNotApplicablePlanningStep({
    changeLoading: (isLoading: boolean) => {
      dispatch(setWizardIsLoading(isLoading))
    },
    partnerId,
    fYear,
  })

  /** -------------------- Variables ----------------------- */

  const { id, translationKey, completeness, isApplicable, isApplicableDisabled } = step

  const stepClassName = useMemo(
    () =>
      cn(styles.wizardStep, {
        [styles.inactive]: id !== activeStepId,
        [styles.active]: id === activeStepId,
      }),
    [id, activeStepId]
  )

  const progressStyle = useMemo(
    () =>
      step
        ? ({
            '--progress': completeness || 0,
            //'--progress-color': step.color, // feature
          } as React.CSSProperties)
        : undefined,
    [completeness, step]
  )

  const { disabledCategoryWarningTranslation, switchOffTranslation } = useMemo(() => {
    const category = step.category

    return {
      disabledCategoryWarningTranslation: `${category}-na-error-message`,
      blockedCategoryWarningTranslation: `${category}-na-blocked-message`,
      switchOffTranslation: `${category}-na-warning-message`,
    }
  }, [step.category])

  /** -------------------- User actions handlers ----------------------- */

  const handleTryToChangeStep = useCallback(async () => {
    if (wizardSave?.isSaveDisabled) {
      dispatch(changeWizardStep({ newStepId: id }))
    } else {
      setIsShowChangeStepConfirmDialog(true)
    }
  }, [dispatch, id, wizardSave?.isSaveDisabled])

  const handleSaveAndChangeStep = useCallback(async () => {
    if (wizardSave) {
      await wizardSave.save()
    }
    setIsShowChangeStepConfirmDialog(false)
    dispatch(changeWizardStep({ newStepId: id }))
  }, [dispatch, id, wizardSave])

  const handleDiscardAndChangeStep = useCallback(() => {
    setIsShowChangeStepConfirmDialog(false)
    dispatch(changeWizardStep({ newStepId: id }))
  }, [dispatch, id])

  const handleApplicableChange = useCallback(
    async (isApplicable: boolean) => {
      if (step.isApplicableDisabled) {
        setIsShowDisabledCategoryWarning(true)

        return
      }

      if (!isApplicable) {
        setIsShowSwitchOnConfirmDialog(true)

        return
      }
    },
    [step]
  )

  const handleConfirmChangeApplicable = useCallback(async () => {
    await updatePlanningStepApplicable(step, isNeedDropCommitment)

    setIsShowSwitchOnConfirmDialog(false)
    setIsNeedDropCommitment(false)
  }, [isNeedDropCommitment, step, updatePlanningStepApplicable])

  const handleCloseChangeStepConfirmDialog = useCallback(() => {
    setIsShowChangeStepConfirmDialog(false)
  }, [])

  const handleCloseConfirmChangeApplicableDialog = useCallback(() => {
    setIsShowSwitchOnConfirmDialog(false)
    setIsNeedDropCommitment(false)
  }, [])

  const handleCloseDisabledCategoryWarningDialog = useCallback(() => {
    setIsShowDisabledCategoryWarning(false)
  }, [])

  /** -------------------- Render ----------------------- */

  return (
    <div onClick={handleTryToChangeStep} className={stepClassName} style={progressStyle}>
      <div className={styles.titleContainer}>
        {IS_SHOW_TEXT_COMPLETENESS && <CompletenessLabel completeness={completeness} />}
        <div>
          <CompletenessIcon completeness={completeness} />
        </div>
        <div className={styles.title}>
          <Translation text={translationKey} span />
        </div>
      </div>
      {!step.isApplicableDisabled && (
        <Switch
          checked={!!isApplicable}
          onChange={handleApplicableChange}
          disabled={!!isApplicableDisabled}
          size={'sm'}
        />
      )}
      <ConfirmationDialog
        isVisible={isShowChangeStepConfirmDialog}
        title={TRANSLATION_KEYS.confirmChangeStep.title}
        content={TRANSLATION_KEYS.confirmChangeStep.message}
        applyKey={TRANSLATION_KEYS.confirmChangeStep.apply}
        onApply={handleSaveAndChangeStep}
        discardKey={TRANSLATION_KEYS.confirmChangeStep.discard}
        onDiscard={handleDiscardAndChangeStep}
        cancelKey={TRANSLATION_KEYS.confirmChangeStep.cancel}
        onCancel={handleCloseChangeStepConfirmDialog}
      />
      <ConfirmationDialog
        isVisible={isShowSwitchOffConfirmDialog}
        title={TRANSLATION_KEYS.confirmCategorySwitchOff.title}
        content={switchOffTranslation}
        applyKey={TRANSLATION_KEYS.confirmCategorySwitchOff.apply}
        onApply={handleConfirmChangeApplicable}
        cancelKey={TRANSLATION_KEYS.confirmCategorySwitchOff.cancel}
        onCancel={handleCloseConfirmChangeApplicableDialog}
        isHideDiscard
      />
      <ConfirmationDialog
        isVisible={isShowDisabledCategoryWarning}
        title={TRANSLATION_KEYS.disabledCategoryWarning.title}
        content={disabledCategoryWarningTranslation}
        applyKey={TRANSLATION_KEYS.disabledCategoryWarning.ok}
        onApply={handleCloseDisabledCategoryWarningDialog}
        isHideDiscard
        isHideCancel
      />
    </div>
  )
})

WizardStep.displayName = 'WizardStep'
